import { useState } from "react";
import logoGoldsand from "@/assets/logo-goldsand-v2.png";

// Plaid OAuth callback fallback.
//
// The canonical Plaid redirect URI is the HTTPS Universal Link:
//   https://link.goldsand.fi/app/plaid-oauth
//
// On the happy path, iOS/Android resolves that Universal/App Link directly into
// Goldsand. The Plaid native SDK then resumes the active Link session from the
// Universal Link; our Expo Router `+native-intent` handler intentionally returns
// `null` for `/app/plaid-oauth` so the app does not navigate to a Goldsand route.
//
// This page is only shown when the Universal/App Link was not claimed by the
// installed app, or the app is not installed. The button below is therefore a
// secondary/manual escape hatch: it opens Goldsand via the custom scheme while
// preserving Plaid's query params.
//
// Important: `goldsand://...` is not the Plaid redirect URI and should not be
// treated as the primary OAuth completion path. It simply foregrounds Goldsand.
// In the app, `goldsand://app/plaid-oauth?...` is normalized to
// `/app/plaid-oauth` and swallowed by `+native-intent`, so tapping this button
// should not navigate the user away from the in-progress Plaid flow. If the
// Plaid SDK still has the active Link handler in memory, foregrounding the app
// lets that flow continue; if the session was lost, the user may need to retry
// linking.
const PLAID_OAUTH_APP_PATH = "/app/plaid-oauth";

function getBackToGoldsandHref() {
  return `goldsand://${PLAID_OAUTH_APP_PATH.slice(1)}${window.location.search}`;
}

export default function PlaidOAuth() {
  const [showInstallHint, setShowInstallHint] = useState(false);

  return (
    <div className="min-h-dvh flex items-center justify-center bg-background text-foreground px-lg">
      <div className="max-w-[420px] w-full flex flex-col items-center text-center gap-md">
        <img
          src={logoGoldsand}
          alt="Goldsand logo"
          className="h-[150px] rounded-[16px] object-cover"
        />
        <h1 className="-mt-6 text-lg font-bold">Finish linking your bank</h1>
        <p className="text-base text-muted-fg leading-relaxed">
          You&apos;re almost done. Open Goldsand to return to Plaid and complete
          your bank connection.
        </p>
        <a
          href={getBackToGoldsandHref()}
          onClick={() => setShowInstallHint(true)}
          className="btn-primary mt-xs h-[48px] px-[32px] rounded-full bg-foreground text-primary-fg text-base font-semibold inline-flex items-center justify-center cursor-pointer shadow-[0_2px_8px_rgba(0,0,0,0.12)]"
        >
          Open Goldsand
        </a>
        {showInstallHint && (
          <p
            role="status"
            aria-live="polite"
            className="text-sm text-muted-fg leading-relaxed"
          >
            If your bank connection doesn't continue automatically, switch back
            to Plaid or try linking again.
          </p>
        )}
      </div>
    </div>
  );
}
